import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { streamSse } from "@/lib/sse";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { fmtDate } from "@/lib/workflow";

export const BriefingCard = () => {
  const [briefing, setBriefing] = useState(null);
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/ai/briefing").then((r) => { setBriefing(r.data.briefing); setModel(r.data.model); setText(r.data.briefing?.text || ""); }).catch(() => {});
  }, []);

  const generate = async () => {
    setStreaming(true); setText("");
    await streamSse("/ai/briefing", null, {
      onDelta: (t) => setText((s) => s + t),
      onDone: (d) => { setBriefing({ text: d.text, created_at: d.created_at }); setStreaming(false); },
      onError: (m) => { toast.error(m); setStreaming(false); },
    });
  };

  return (
    <Card className="p-5 border-slate-200 bg-white relative overflow-hidden" data-testid="briefing-card">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
        <div className="h-12 w-12 shrink-0 rounded-full bg-slate-900 grid place-items-center"><Sparkles className="h-5 w-5 text-white" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-slate-900">Daily Briefing</h3>
            <Badge variant="outline" className="font-mono text-[10px] uppercase">{model || "AI"}</Badge>
            {briefing?.created_at && !streaming && <span className="text-[11px] text-slate-400 font-mono">{fmtDate(briefing.created_at)}</span>}
            {streaming && <span className="text-[11px] text-blue-600 animate-pulse">Generating…</span>}
          </div>
          {text ? (
            <>
              <p className={`mt-2 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed ${expanded || streaming ? "" : "line-clamp-2"}`} data-testid="briefing-text">{text}{streaming && <span className="inline-block w-1.5 h-4 bg-slate-900 ml-0.5 animate-pulse align-middle" />}</p>
              {!streaming && (
                <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[#2563EB] hover:underline" data-testid="briefing-toggle-btn">
                  {expanded ? <><ChevronUp className="h-3.5 w-3.5" />Show less</> : <><ChevronDown className="h-3.5 w-3.5" />Read full briefing</>}
                </button>
              )}
            </>
          ) : (
            <p className="mt-1 text-sm text-slate-500">AI summary of your most urgent next actions, overdue tasks, blocked phases and ERP-ready projects — generated from live data.</p>
          )}
        </div>
        </div>
        <Button variant="outline" className="shrink-0 border-slate-300 w-full sm:w-auto" onClick={generate} disabled={streaming} data-testid="briefing-generate-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${streaming ? "animate-spin" : ""}`} />{briefing ? "Refresh briefing" : "Generate briefing"}
        </Button>
      </div>
    </Card>
  );
};
